import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
    Trash2, 
    CreditCard,
    Calendar
} from 'lucide-react'
// import { paymentAPI, orderAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Payments() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMode, setSelectedMode] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingPayment, setEditingPayment] = useState(null)

  const queryClient = useQueryClient()

  // Fetch payments
  const { data: paymentsData, isLoading } = useQuery(
    ['payments', { page: currentPage, search: searchTerm, paymentMode: selectedMode }],
    () => paymentAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      paymentMode: selectedMode,
    })
  )

  // Fetch orders for form
  const { data: ordersData } = useQuery('orders', () => orderAPI.getAll({ limit: 100 }))

  // Create payment mutation
  const createPaymentMutation = useMutation(paymentAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('payments')
      setShowModal(false)
      toast.success('Payment created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create payment')
    },
  })

  // Update payment mutation
  const updatePaymentMutation = useMutation(
    ({ id, data }) => paymentAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payments')
        setShowModal(false)
        setEditingPayment(null)
        toast.success('Payment updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update payment')
      },
    }
  )

  // Delete payment mutation
  const deletePaymentMutation = useMutation(paymentAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('payments')
      toast.success('Payment deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete payment')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingPayment) {
      updatePaymentMutation.mutate({ id: editingPayment.id, data })
    } else {
      createPaymentMutation.mutate(data)
    }
  }

  const handleEdit = (payment) => {
    setEditingPayment(payment)
    reset({
      orderId: payment.orderId,
      amount: payment.amount,
      paymentMode: payment.paymentMode,
      paymentDate: payment.paymentDate,
      transactionId: payment.transactionId,
      notes: payment.notes,
    })
    setShowModal(true)
  }

  const handleDelete = (payment) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      deletePaymentMutation.mutate(payment.id)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingPayment(null)
    reset()
  }

  const payments = paymentsData?.data?.payments || []
  const orders = ordersData?.data?.orders || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Payments</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage payment records and transactions
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Payment
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search payments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Payment Mode</label>
              <select
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                className="input"
              >
                <option value="">All Modes</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedMode('')
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Order</th>
                    <th className="table-head">Amount</th>
                    <th className="table-head">Payment Mode</th>
                    <th className="table-head">Transaction ID</th>
                    <th className="table-head">Date</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {payment.order?.orderNumber}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {payment.order?.customer?.firstName} {payment.order?.customer?.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <CreditCard className="h-4 w-4 mr-1" />
                          ₹{payment.amount}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-secondary">
                          {payment.paymentMode}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {payment.transactionId || 'N/A'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(payment.paymentDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(payment)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(payment)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingPayment ? 'Edit Payment' : 'Add New Payment'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">Order</label>
                  <select
                    {...register('orderId', { required: 'Order is required' })}
                    className="input"
                  >
                    <option value="">Select an order</option>
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.orderNumber} - {order.customer?.firstName} {order.customer?.lastName} - ₹{order.totalAmount}
                      </option>
                    ))}
                  </select>
                  {errors.orderId && (
                    <p className="form-error">{errors.orderId.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Amount</label>
                    <input
                      {...register('amount', { required: 'Amount is required' })}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter amount"
                    />
                    {errors.amount && (
                      <p className="form-error">{errors.amount.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Payment Mode</label>
                    <select
                      {...register('paymentMode', { required: 'Payment mode is required' })}
                      className="input"
                    >
                      <option value="">Select payment mode</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                    {errors.paymentMode && (
                      <p className="form-error">{errors.paymentMode.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Payment Date</label>
                    <input
                      {...register('paymentDate', { required: 'Payment date is required' })}
                      type="datetime-local"
                      className="input"
                    />
                    {errors.paymentDate && (
                      <p className="form-error">{errors.paymentDate.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Transaction ID</label>
                    <input
                      {...register('transactionId')}
                      className="input"
                      placeholder="Enter transaction ID"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Notes</label>
                  <textarea
                    {...register('notes')}
                    className="input"
                    rows={3}
                    placeholder="Enter payment notes"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createPaymentMutation.isLoading || updatePaymentMutation.isLoading}
                    className="btn-primary"
                  >
                    {createPaymentMutation.isLoading || updatePaymentMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingPayment ? 'Update Payment' : 'Create Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payments
